import React, { useState, Component } from 'react';
import { Button } from './Button';
import { Check } from 'lucide-react';
interface LeadCaptureFormProps {
  onSubmit?: (data: FormData) => void;
  submitEmail?: string;
}
interface FormData {
  name: string;
  email: string;
  phone: string;
  experience: string;
  goals: string;
}
export function LeadCaptureForm({
  onSubmit,
  submitEmail = 'contact@szulcaleks.com'
}: LeadCaptureFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    experience: '',
    goals: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Custom handler if provided
    if (onSubmit) {
      onSubmit(formData);
    } else {
      // Default: Create mailto link with form data
      const subject = encodeURIComponent('New Training Inquiry from ' + formData.name);
      const body = encodeURIComponent(`Name: ${formData.name}\n` + `Email: ${formData.email}\n` + `Phone: ${formData.phone}\n` + `Experience Level: ${formData.experience}\n` + `Goals:\n${formData.goals}`);
      window.location.href = `mailto:${submitEmail}?subject=${subject}&body=${body}`;
    }
    // Show success message
    setTimeout(() => {
      setIsSubmitted(true);
      setIsSubmitting(false);
    }, 500);
  };
  if (isSubmitted) {
    return <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 md:p-12 text-center" data-id="element-76">
        <div className="w-16 h-16 bg-[#D4FF00] rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-77">
          <Check className="w-8 h-8 text-black" strokeWidth={3} data-id="element-78" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-4" data-id="element-79">
          Thanks! I'll get back to you soon.
        </h3>
        <p className="text-gray-300 mb-6" data-id="element-80">
          I've received your information and will reach out within 24 hours to
          discuss your training goals.
        </p>
        <Button variant="outline" onClick={() => setIsSubmitted(false)} data-id="element-81">
          Submit Another Request
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-2xl p-8 md:p-12 space-y-6" data-id="element-82">
      {/* Name Field */}
      <div data-id="element-83">
        <label htmlFor="name" className="block text-sm font-medium text-white mb-2" data-id="element-84">
          Full Name *
        </label>
        <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent transition-all" placeholder="Enter your full name" data-id="element-85" />
      </div>

      {/* Email Field */}
      <div data-id="element-86">
        <label htmlFor="email" className="block text-sm font-medium text-white mb-2" data-id="element-87">
          Email Address *
        </label>
        <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent transition-all" placeholder="your.email@example.com" data-id="element-88" />
      </div>

      {/* Phone Field */}
      <div data-id="element-89">
        <label htmlFor="phone" className="block text-sm font-medium text-white mb-2" data-id="element-90">
          Phone Number (Optional)
        </label>
        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent transition-all" placeholder="+48 123 456 789" data-id="element-91" />
      </div>

      {/* Experience Level Dropdown */}
      <div data-id="element-92">
        <label htmlFor="experience" className="block text-sm font-medium text-white mb-2" data-id="element-93">
          Experience Level *
        </label>
        <select id="experience" name="experience" required value={formData.experience} onChange={handleChange} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent transition-all" data-id="element-94">
          <option value="" data-id="element-95">Select your experience level</option>
          <option value="complete-beginner" data-id="element-96">Complete Beginner</option>
          <option value="some-experience" data-id="element-97">Some Experience (1-2 years)</option>
          <option value="intermediate" data-id="element-98">Intermediate (3-5 years)</option>
          <option value="advanced" data-id="element-99">Advanced (5+ years)</option>
        </select>
      </div>

      {/* Goals Textarea */}
      <div data-id="element-100">
        <label htmlFor="goals" className="block text-sm font-medium text-white mb-2" data-id="element-101">
          Your Goals *
        </label>
        <textarea id="goals" name="goals" required value={formData.goals} onChange={handleChange} rows={4} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent transition-all resize-none" placeholder="Tell me about your fitness goals, what you want to achieve, and any specific areas you'd like to focus on..." data-id="element-102" />
      </div>

      {/* Submit Button */}
      <div className="pt-4" data-id="element-103">
        <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting} data-id="element-104">
          {isSubmitting ? 'Submitting...' : 'Submit Application'}
        </Button>
        <p className="text-xs text-gray-400 text-center mt-4" data-id="element-105">
          * Required fields. Your information is kept confidential.
        </p>
      </div>
    </form>;
}